import { useCallback } from "react";
import useSWR from "swr";

import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import {
  BrandAssetVariableRequest,
  BrandAssetVariableResponse,
  CorporateBrandAssetDto,
  CorporateBrandAssetRequest,
} from "../types";
import { GLOBAL_CORPORATE_ID } from "../constants";

export const useBrandAssets = (corporateId: number) => {
  const isGlobalScope = corporateId === GLOBAL_CORPORATE_ID;

  const listKey = corporateId
    ? `${ServicesURL.getCorporateBrandAssetList}/${corporateId}`
    : null;

  const {
    data: list,
    isLoading,
    error,
    mutate,
  } = useSWR(listKey ? [listKey, MethodType.GET] : null, ([url, method]) =>
    fetcher(url, method),
  );

  const { data: globalList } = useSWR(
    !isGlobalScope
      ? [
          `${ServicesURL.getCorporateBrandAssetList}/${GLOBAL_CORPORATE_ID}`,
          MethodType.GET,
        ]
      : null,
    ([url, method]) => fetcher(url, method),
  );

  const createBrandAsset = useCallback(
    (body: CorporateBrandAssetRequest) =>
      fetcher(ServicesURL.createCorporateBrandAsset, MethodType.POST, body),
    [],
  );

  const editBrandAsset = useCallback(
    (id: number, body: CorporateBrandAssetRequest) =>
      fetcher(
        `${ServicesURL.editCorporateBrandAsset}/${id}`,
        MethodType.PUT,
        body,
      ),
    [],
  );

  const deleteBrandAsset = useCallback(
    (id: number) =>
      fetcher(
        `${ServicesURL.deleteCorporateBrandAsset}/${id}`,
        MethodType.DELETE,
        { corporateId },
      ),
    [corporateId],
  );

  const createBrandAssetVariable = useCallback(
    (body: BrandAssetVariableRequest): Promise<BrandAssetVariableResponse> =>
      fetcher(
        ServicesURL.createCorporateBrandAssetVariable,
        MethodType.POST,
        body,
      ),
    [],
  );

  const asArray = (raw: any): CorporateBrandAssetDto[] =>
    Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];

  return {
    list: asArray(list),
    globalList: asArray(isGlobalScope ? list : globalList),
    isGlobalScope,
    isLoading,
    error,
    mutate,
    createBrandAsset,
    editBrandAsset,
    deleteBrandAsset,
    createBrandAssetVariable,
  };
};
